import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FeedbackWidget, helperText, askHelper } from "@/components/FeedbackWidget";
import { useLocation } from "react-router-dom";
import { Building2, LogOut, ChevronDown, Palette } from "lucide-react";
import { AppShell } from "@/components/shell/AppShell";
import { PreviewAsPicker, ViewerBanner } from "@/components/ViewerPreview";
import { GmailCard } from "@/components/EmailInbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function Layout({ children }) {
  const { user, logout, activeRole, setActiveRole, newLook, setNewLook } = useAuth();
  const navigate = useNavigate();
  const roles = user?.roles || [];
  const loc = useLocation();
  const isPartner = roles.includes("Partner") && !roles.some((r) => ["RM", "Manager", "Admin"].includes(r));
  const isManagerView = activeRole === "Manager" || activeRole === "Admin";
  const hasRm = roles.includes("RM") || activeRole === "RM";
  const home = isPartner ? "/partner" : (hasRm ? "/my-day" : "/standup");
  const switchRole = (r) => { setActiveRole(r); navigate(r === "RM" || roles.includes("RM") ? "/my-day" : "/standup"); };

  useEffect(() => { document.title = isPartner ? "Partner Portal · Truva HL" : "RM Desk · Truva HL"; }, [isPartner]);
  // Batch F: "New look" users get the v2 shell (sidebar + top bar); everyone else sees today's header, unchanged
  if (newLook && !isPartner) return <AppShell>{children}</AppShell>;
  return (
    <div className="min-h-screen bg-[#F6F5EF] text-[#333333]">
      {/* Truva header: Black BG #222222, orange brand accent */}
      <header className="sticky top-0 z-50 h-14 bg-[#222222] text-white flex items-center justify-between px-6 border-b border-[#333333] whitespace-nowrap overflow-x-auto">
        <div
          className="flex items-center gap-2 font-semibold text-base tracking-tight cursor-pointer flex-shrink-0"
          onClick={() => navigate(home)}
          data-testid="brand-home"
        >
          <Building2 className="w-5 h-5 text-[#FF6F38]" />
          Truva HL <span className="text-[#C5C4C4] font-normal">· {isPartner ? "Partner Portal" : "RM Desk"}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <PreviewAsPicker dark />
          {!isPartner && (
            <nav className="flex items-center gap-1 mr-2 flex-shrink-0">
              {hasRm && (
                <button onClick={() => navigate("/my-day")} data-testid="nav-my-day" className="text-xs text-[#C5C4C4] hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-[#333333] transition-colors">My Day</button>
              )}
              <button onClick={() => navigate("/standup")} data-testid="nav-standup" className="text-xs text-[#C5C4C4] hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-[#333333] transition-colors">Standup</button>
              <button onClick={() => navigate("/leaderboard")} data-testid="nav-leaderboard" className="text-xs text-[#C5C4C4] hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-[#333333] transition-colors">🏆 Leaderboard</button>
              {isManagerView && (
                <>
                  <button onClick={() => navigate("/wire")} data-testid="nav-weekly" className="text-xs text-[#C5C4C4] hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-[#333333] transition-colors">Weekly</button>
                </>
              )}
              <button onClick={() => navigate("/cases")} data-testid="nav-cases" className="text-xs text-[#C5C4C4] hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-[#333333] transition-colors">Cases</button>
              {(roles.includes("Manager") || roles.includes("Admin")) && activeRole !== "RM" && (
                <button onClick={() => navigate("/admin")} data-testid="nav-admin" className="text-xs text-[#C5C4C4] hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-[#333333] transition-colors">
                  {roles.includes("Admin") ? "Admin" : "Manager"}
                </button>
              )}
              {roles.includes("Admin") && activeRole === "Admin" && (
                <>
                  <button onClick={() => navigate("/admin/preview-partner")} data-testid="nav-preview-partner" className="text-xs text-[#C5C4C4] hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-[#333333] transition-colors">
                    Preview as partner
                  </button>
                </>
              )}
            </nav>
          )}
          {roles.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="role-switcher"
                  className="flex items-center gap-1.5 text-xs bg-[#333333] text-[#E2E2E2] px-3 py-1.5 rounded-lg border border-[#444444] hover:bg-[#3d3d3d] transition-colors"
                >
                  Viewing as: <span className="font-medium text-white">{activeRole}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white rounded-xl border-[#E2E2E2]">
                <DropdownMenuLabel className="text-xs">Switch role view</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {roles.map((r) => (
                  <DropdownMenuItem
                    key={r}
                    data-testid={`role-option-${r}`}
                    onClick={() => switchRole(r)}
                    className="text-sm cursor-pointer"
                  >
                    {r}
                  </DropdownMenuItem>
                ))}
                {!isPartner && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => setNewLook(true)} data-testid="new-look-on" className="text-sm cursor-pointer"><Palette className="w-3.5 h-3.5 mr-2 text-[#FF4802]" /> Try the new look</DropdownMenuItem></>}
                {!isPartner && <div className="p-2 pt-1 w-72"><GmailCard /></div>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <div className="flex items-center gap-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-[#444444]" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-[#FF4802] flex items-center justify-center text-xs font-semibold text-white">
                {user?.name?.[0] || "?"}
              </div>
            )}
            <div className="text-xs leading-tight hidden sm:block">
              <div className="font-medium">{user?.name}</div>
              <div className="text-[#C5C4C4]">{user?.email}</div>
            </div>
          </div>
          <button onClick={() => askHelper(helperText(loc.pathname, window.__truvaCaseId || "", ""))} data-testid="header-help" title="Ask the RM Desk helper in Claude" className="text-[#C5C4C4] hover:text-white w-7 h-7 rounded-full border border-[#444] text-xs font-semibold mr-2">?</button>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="flex items-center gap-1.5 text-xs text-[#C5C4C4] hover:text-white px-2 py-1.5 rounded-lg hover:bg-[#333333] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>
      <ViewerBanner />
      <main className="w-full max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16 py-6 pb-24">
        {children}
      </main>
      <FeedbackWidget />
    </div>
  );
}


